import { useEffect, useRef } from "react";

/**
 * Historial del navegador para el teléfono: cada hoja o pantalla abierta ocupa una entrada,
 * así que el botón Atrás del sistema la cierra en vez de salir de la página.
 */

const layers: string[] = [];
const waiting: Array<() => void> = [];
let pendingBacks = 0;
let programmatic = false;
let topAtPop: string | undefined;
let installed = false;

function install() {
  if (installed) return;
  installed = true;
  // En captura: corre antes que los demás oyentes de popstate y deja marcado de dónde viene el evento.
  window.addEventListener(
    "popstate",
    () => {
      topAtPop = layers[layers.length - 1];
      programmatic = pendingBacks > 0;
      if (programmatic) pendingBacks -= 1;
      if (pendingBacks === 0 && waiting.length > 0) {
        const fns = waiting.splice(0);
        queueMicrotask(() => fns.forEach((fn) => fn()));
      }
    },
    true,
  );
}

/** El popstate en curso lo provocó la propia interfaz (cerrar con un botón), no la persona con Atrás. */
export const popWasProgrammatic = () => programmatic;

function uiBack() {
  pendingBacks += 1;
  history.back();
}

/** Ejecuta `fn` cuando no quede ningún retroceso de la interfaz en vuelo. */
export function afterBacks(fn: () => void) {
  install();
  if (pendingBacks === 0) fn();
  else waiting.push(fn);
}

/** Mientras `open` sea verdadero, Atrás cierra esta capa. Solo responde la capa de más arriba. */
export function useBackClose(open: boolean, onClose: () => void) {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    if (!open) return;
    install();
    const token = `${Date.now()}-${Math.random()}`;
    layers.push(token);
    history.pushState({ ...(history.state ?? {}), sbxLayer: token }, "");
    let handled = false;
    const onPop = () => {
      if (programmatic || topAtPop !== token) return;
      handled = true;
      const i = layers.indexOf(token);
      if (i >= 0) layers.splice(i, 1);
      ref.current();
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      const i = layers.indexOf(token);
      if (i >= 0) layers.splice(i, 1);
      if (!handled && history.state?.sbxLayer === token) uiBack();
    };
  }, [open]);
}

/** Atrás vuelve a la vista anterior de la barra inferior. `set` recibe la vista de la entrada a la que se llegó. */
export function useViewHistory(active: string, set: (view: string) => void, enabled: boolean) {
  const fromPop = useRef(false);
  const first = useRef(true);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (!enabled) return;
    install();
    const onPop = (e: PopStateEvent) => {
      if (programmatic) return;
      const view = (e.state as { sbxView?: string } | null)?.sbxView;
      if (view && view !== activeRef.current) {
        fromPop.current = true;
        set(view);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [enabled, set]);

  useEffect(() => {
    if (!enabled) return;
    if (fromPop.current) {
      fromPop.current = false;
      return;
    }
    if (first.current) {
      first.current = false;
      history.replaceState({ ...(history.state ?? {}), sbxView: active }, "");
      return;
    }
    afterBacks(() => {
      if ((history.state as { sbxView?: string } | null)?.sbxView === active) return;
      history.pushState({ ...(history.state ?? {}), sbxView: active, sbxLayer: undefined }, "");
    });
  }, [active, enabled]);
}
